import { useEffect, useMemo } from 'react';
import { IntlProvider } from 'react-intl';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MantineProvider } from '@mantine/core';
import { PersistGate } from 'redux-persist/integration/react';
import { persistor } from './store/store';
import axios from './axios-instance';
import 'react-toastify/dist/ReactToastify.css';
import { messages } from './translations';
import Routes from './routes/routes';
import { getLangState } from './store/lang.slice';
import { useSelector } from 'react-redux';
import ScrollToTop from './ScrollTop';
import { usePreventKeyboardScroll } from './hooks/usePreventKeyboardScroll';

function App() {
  /* ------------------------------- Local State ------------------------------ */
  const lang = useSelector(getLangState);
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: false,
          },
        },
      }),
    []
  );

  // Prevent iOS Safari keyboard scroll issues globally
  usePreventKeyboardScroll();

  /* --------------------------------- Effects -------------------------------- */
  useEffect(() => {
    axios.defaults.headers.common['Accept-Language'] = lang;
    queryClient.invalidateQueries();
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    localStorage.setItem('lang', lang);
  }, [lang, queryClient]);

  
  window.addEventListener('error', (e) => {
    if (e.message.includes('Failed to fetch dynamically imported module')) {
      window.location.reload();
    }
  });

  return (
    <div className="App">
      <ScrollToTop />
      <PersistGate loading={null} persistor={persistor}>
        <IntlProvider locale={lang} messages={messages[lang]}>
          <QueryClientProvider client={queryClient}>
            <MantineProvider withGlobalStyles withNormalizeCSS>
              <Routes />
            </MantineProvider>
          </QueryClientProvider>
        </IntlProvider>
      </PersistGate>
    </div>
  );
}

export default App;
